import { Injectable } from '@angular/core';

/**
 * Supported languages in Ford Protect application.
 * Each language maps to a specific locale code used throughout the app.
 */
export enum SupportedLanguage {
	PT_BR = 'pt-BR',
	EN_US = 'en-US',
	ES_CO = 'es-CO',
	ES_CL = 'es-CL',
	ES_AR = 'es-AR',
	ES_VE = 'es-VE'
}

/**
 * Country codes supported by the application.
 */
export enum SupportedCountry {
	BRA = 'BRA', // Brazil
	ARG = 'ARG', // Argentina
	CHL = 'CHL', // Chile
	COL = 'COL', // Colombia
	VEN = 'VEN'  // Venezuela
}

/**
 * Language configuration metadata.
 * Maps language codes to their file paths and display labels.
 */
export interface LanguageConfig {
	code: SupportedLanguage;
	filePath: string;
	label: string;
	direction: 'ltr' | 'rtl';
}

/**
 * Maps country to its default language.
 */
export interface CountryLanguageMap {
	[key: string]: SupportedLanguage;
}

/**
 * Central i18n configuration service.
 * 
 * Consolidates all i18n-related configuration in a single, typed source of truth.
 * Provides methods to:
 * - Get supported languages and their metadata
 * - Map countries to their default languages
 * - Validate language codes
 * - Get language configuration by code
 * 
 * This service has NO side effects — it's a pure configuration provider.
 * Use I18nBrokerService for language changes and LOCALE_ID synchronization.
 */
@Injectable({
	providedIn: 'root'
})
export class I18nConfigService {

	/**
	 * Language configurations indexed by language code.
	 * Single source of truth for language metadata.
	 */
	private readonly languageConfigs: Map<SupportedLanguage, LanguageConfig> = new Map([
		[SupportedLanguage.PT_BR, {
			code: SupportedLanguage.PT_BR,
			filePath: 'pt-BR',
			label: 'Português (Brasil)',
			direction: 'ltr'
		}],
		[SupportedLanguage.EN_US, {
			code: SupportedLanguage.EN_US,
			filePath: 'en-US',
			label: 'English (United States)',
			direction: 'ltr'
		}],
		[SupportedLanguage.ES_CO, {
			code: SupportedLanguage.ES_CO,
			filePath: 'es-CO',
			label: 'Español (Colombia)',
			direction: 'ltr'
		}],
		[SupportedLanguage.ES_CL, {
			code: SupportedLanguage.ES_CL,
			filePath: 'es-CL',
			label: 'Español (Chile)',
			direction: 'ltr'
		}],
		[SupportedLanguage.ES_AR, {
			code: SupportedLanguage.ES_AR,
			filePath: 'es-AR',
			label: 'Español (Argentina)',
			direction: 'ltr'
		}],
		[SupportedLanguage.ES_VE, {
			code: SupportedLanguage.ES_VE,
			filePath: 'es-VE',
			label: 'Español (Venezuela)',
			direction: 'ltr'
		}]
	]);

	/**
	 * Country to default language mapping.
	 * Used to auto-set language based on user's country.
	 */
	private readonly countryLanguageMap: CountryLanguageMap = {
		[SupportedCountry.BRA]: SupportedLanguage.PT_BR,
		[SupportedCountry.ARG]: SupportedLanguage.ES_AR,
		[SupportedCountry.CHL]: SupportedLanguage.ES_CL,
		[SupportedCountry.COL]: SupportedLanguage.ES_CO,
		[SupportedCountry.VEN]: SupportedLanguage.ES_VE
	};

	/**
	 * Get all supported language configurations.
	 * @returns Array of language configurations
	 */
	getSupportedLanguages(): LanguageConfig[] {
		return Array.from(this.languageConfigs.values());
	}

	/**
	 * Get a specific language configuration by code.
	 * @param language - Language code
	 * @returns Language configuration or undefined if not found
	 */
	getLanguageConfig(language: SupportedLanguage): LanguageConfig | undefined {
		return this.languageConfigs.get(language);
	}

	/**
	 * Get the default language for a given country.
	 * @param country - Country code
	 * @returns Default language for the country, or undefined if country not found
	 */
	getDefaultLanguageByCountry(country: string): SupportedLanguage | undefined {
		return this.countryLanguageMap[country];
	}

	/**
	 * Check if a language code is supported.
	 * @param language - Language code to validate
	 * @returns true if language is supported, false otherwise
	 */
	isSupportedLanguage(language: string): language is SupportedLanguage {
		return Object.values(SupportedLanguage).includes(language as SupportedLanguage);
	}

	/**
	 * Check if a country code is supported.
	 * @param country - Country code to validate
	 * @returns true if country is supported, false otherwise
	 */
	isSupportedCountry(country: string): country is SupportedCountry {
		return Object.values(SupportedCountry).includes(country as SupportedCountry);
	}

	/**
	 * Get the translation file path for a language.
	 * @param language - Language code
	 * @returns Relative path to the translation file (without extension)
	 */
	getTranslationFilePath(language: SupportedLanguage): string {
		const config = this.languageConfigs.get(language);
		return config ? config.filePath : '';
	}

	/**
	 * Get the display label for a language.
	 * @param language - Language code
	 * @returns Human-readable language label
	 */
	getLanguageLabel(language: SupportedLanguage): string {
		const config = this.languageConfigs.get(language);
		return config ? config.label : '';
	}

	/**
	 * Get the default language used as fallback.
	 * @returns Default language code
	 */
	getDefaultLanguage(): SupportedLanguage {
		return SupportedLanguage.PT_BR;
	}

	/**
	 * Get all supported languages as simple array of codes.
	 * @returns Array of language codes
	 */
	getLanguageCodes(): SupportedLanguage[] {
		return Array.from(this.languageConfigs.keys());
	}

	/**
	 * Get all supported countries as array of codes.
	 * @returns Array of country codes
	 */
	getSupportedCountries(): SupportedCountry[] {
		return Object.values(SupportedCountry);
	}
}
